import React, { useCallback, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faCopy,
  faCheck,
  faExternalLinkAlt,
  faSync,
} from "@fortawesome/free-solid-svg-icons";
import {
  paymentService,
  formatCurrency,
  formatApiError,
} from "../../../../services/api";
import { useAuth } from "../../../../context/authContext";
import { useBonuses } from "../../../../context/bonusContext";
import styles from "./PaycashInstructionsModal.module.css";

const STATUS_LABELS = {
  PENDING: "Pendiente de pago",
  APPROVED: "Acreditada",
  EXPIRED: "Expirada",
  CANCELLED: "Cancelada",
  REJECTED: "Rechazada",
  ERROR: "Error",
};

function statusClass(status) {
  const s = String(status || "").toUpperCase();
  if (s === "APPROVED") return styles.statusApproved;
  if (s === "PENDING") return styles.statusPending;
  if (s === "CANCELLED" || s === "EXPIRED" || s === "REJECTED") {
    return styles.statusCancelled;
  }
  return styles.statusPending;
}

/**
 * Instrucciones de pago PayCash: referencia, abrir URL, polling y cancelar.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.recharge — datos de create_recharge o historial
 * @param {() => void} [props.onStatusChange] — notificar al padre (p. ej. refrescar historial)
 */
export default function PaycashInstructionsModal({
  isOpen,
  onClose,
  recharge,
  onStatusChange,
}) {
  const { loadProfile, updateBalance } = useAuth();
  const { detectNewWelcomeBonus } = useBonuses();
  const [status, setStatus] = useState(recharge?.status || "PENDING");
  const [checking, setChecking] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const pollRef = useRef(null);
  const approvedHandled = useRef(false);
  const statusRef = useRef(String(recharge?.status || "PENDING").toUpperCase());
  const onStatusChangeRef = useRef(onStatusChange);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    onStatusChangeRef.current = onStatusChange;
  }, [onStatusChange]);

  const transactionId =
    recharge?.transaction_id || recharge?.transactionId || recharge?.id;
  const paycashReference =
    recharge?.paycash_reference || recharge?.paycashReference || "";
  const paymentUrl =
    recharge?.payment_instructions_url ||
    recharge?.paymentInstructionsUrl ||
    "";
  const amount = recharge?.amount;
  const expirationDate =
    recharge?.expiration_date || recharge?.expirationDate || "";

  const refreshBalance = useCallback(async () => {
    const profile = await loadProfile();
    const amountVal = profile?.balance?.amount ?? profile?.balance;
    if (amountVal != null) {
      updateBalance(amountVal);
    }
  }, [loadProfile, updateBalance]);

  const handleApproved = useCallback(async () => {
    if (approvedHandled.current) return;
    approvedHandled.current = true;
    setMessage("¡Saldo acreditado correctamente!");
    await refreshBalance();
    onStatusChangeRef.current?.("APPROVED");
    // Primera recarga ≥ mínimo → bono de bienvenida: si apareció uno nuevo,
    // el BonusProvider abre el modal de celebración ("¡Ganaste 1 fracción gratis!").
    detectNewWelcomeBonus().then((bonus) => {
      if (bonus) onCloseRef.current?.();
    });
  }, [refreshBalance, detectNewWelcomeBonus]);

  const checkStatus = useCallback(
    async ({ showLoading = true } = {}) => {
      if (!transactionId) return;
      if (showLoading) setChecking(true);
      setError("");
      try {
        const result = await paymentService.getPaymentStatus(transactionId);
        if (result.ok && result.data) {
          const next = String(result.data.status || "").toUpperCase();
          const prev = statusRef.current;
          statusRef.current = next;
          setStatus(next);
          if (next === "APPROVED") {
            await handleApproved();
          } else {
            if (next === "PENDING" && showLoading) {
              setMessage(
                "El pago aún está pendiente. Si pagaste con Nequi u otro canal digital, puede tardar unos minutos."
              );
            }
            if (next !== prev) {
              onStatusChangeRef.current?.(next);
            }
          }
        } else {
          setError(formatApiError(result.data) || "No se pudo consultar el estado");
        }
      } catch (err) {
        setError("Error de conexión al consultar el estado");
      } finally {
        if (showLoading) setChecking(false);
      }
    },
    [transactionId, handleApproved]
  );

  // Sync when opening with new recharge
  useEffect(() => {
    if (!isOpen || !recharge) return;
    approvedHandled.current = false;
    const initial = String(recharge.status || "PENDING").toUpperCase();
    statusRef.current = initial;
    setStatus(initial);
    setMessage("");
    setError("");
    setCopied(false);
  }, [isOpen, recharge]);

  // Polling mientras PENDING
  useEffect(() => {
    if (!isOpen || !transactionId) return undefined;

    const stop = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    if (String(status).toUpperCase() !== "PENDING") {
      stop();
      return stop;
    }

    checkStatus({ showLoading: false });
    pollRef.current = setInterval(() => {
      checkStatus({ showLoading: false });
    }, 20000);

    return stop;
  }, [isOpen, transactionId, status, checkStatus]);

  // Al volver el foco / visibilidad
  useEffect(() => {
    if (!isOpen || String(status).toUpperCase() !== "PENDING") return undefined;

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        checkStatus({ showLoading: false });
      }
    };
    const onFocus = () => checkStatus({ showLoading: false });

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [isOpen, status, checkStatus]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen || !recharge) return null;

  const handleCopy = async () => {
    if (!paycashReference) return;
    try {
      await navigator.clipboard.writeText(String(paycashReference));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("No se pudo copiar la referencia");
    }
  };

  const handlePay = () => {
    if (!paymentUrl) {
      setError("No hay URL de instrucciones de pago");
      return;
    }
    window.open(paymentUrl, "_blank", "noopener,noreferrer");
  };

  const handleCancel = async () => {
    if (!transactionId) return;
    const confirmed = window.confirm(
      "¿Cancelar esta recarga pendiente? La referencia dejará de ser válida."
    );
    if (!confirmed) return;

    setCancelling(true);
    setError("");
    try {
      const result = await paymentService.cancelRecharge(transactionId);
      if (result.ok && result.outcome === "cancelled") {
        statusRef.current = "CANCELLED";
        setStatus("CANCELLED");
        setMessage(result.message || "Recarga cancelada correctamente.");
        onStatusChangeRef.current?.("CANCELLED");
      } else if (result.ok && result.outcome === "alreadyApproved") {
        statusRef.current = "APPROVED";
        setStatus("APPROVED");
        setMessage(result.message);
        await handleApproved();
      } else {
        setError(result.message || formatApiError(result.data));
      }
    } catch {
      setError("Error de conexión al cancelar");
    } finally {
      setCancelling(false);
    }
  };

  const isPending = String(status).toUpperCase() === "PENDING";
  const statusLabel =
    STATUS_LABELS[String(status).toUpperCase()] || status || "—";

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="paycash-instructions-title"
      >
        <div className={styles.modalHeader}>
          <h2 id="paycash-instructions-title" className={styles.modalTitle}>
            Instrucciones de pago
          </h2>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Cerrar"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className={styles.modalBody}>
          <p className={styles.hint}>
            Paga con la referencia PayCash en puntos físicos o canales digitales.
            El saldo se acredita cuando PayCash confirme el pago.
          </p>

          <div className={`${styles.statusBadge} ${statusClass(status)}`}>
            {statusLabel}
          </div>

          <div className={styles.referenceBlock}>
            <span className={styles.referenceLabel}>Referencia PayCash</span>
            <div className={styles.referenceRow}>
              <span className={styles.referenceValue}>
                {paycashReference || "—"}
              </span>
              <button
                type="button"
                className={styles.copyBtn}
                onClick={handleCopy}
                disabled={!paycashReference}
                title="Copiar referencia"
              >
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
              </button>
            </div>
          </div>

          <div className={styles.metaGrid}>
            <div>
              <span className={styles.metaLabel}>Monto</span>
              <span className={styles.metaValue}>
                {formatCurrency(amount)}
              </span>
            </div>
            {expirationDate ? (
              <div>
                <span className={styles.metaLabel}>Vence</span>
                <span className={styles.metaValue}>{expirationDate}</span>
              </div>
            ) : null}
          </div>

          {message ? <p className={styles.successText}>{message}</p> : null}
          {error ? <p className={styles.errorText}>{error}</p> : null}

          <div className={styles.actions}>
            {isPending && paymentUrl ? (
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={handlePay}
              >
                <FontAwesomeIcon icon={faExternalLinkAlt} />
                Pagar
              </button>
            ) : null}

            {isPending ? (
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={() => checkStatus({ showLoading: true })}
                disabled={checking}
              >
                <FontAwesomeIcon icon={faSync} spin={checking} />
                {checking ? "Consultando…" : "Actualizar estado"}
              </button>
            ) : null}

            {isPending ? (
              <button
                type="button"
                className={styles.cancelBtn}
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? "Cancelando…" : "Cancelar recarga"}
              </button>
            ) : null}

            {!isPending ? (
              <button
                type="button"
                className={styles.primaryBtn}
                onClick={onClose}
              >
                Cerrar
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
